import { Badge, Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import type { Translate } from './customer-types'

/**
 * Props for {@link CustomerHeaderView}. Each action handler is present only
 * when the viewer may perform it; an anonymised customer gets none.
 */
export interface CustomerHeaderViewProps {
	t: Translate
	fullName: string
	anonymized: boolean
	type: 'lead' | 'customer'
	status: 'active' | 'inactive'
	externalId: string | null
	source: string
	onEdit?: () => void
	onReassign?: () => void
	onConvert?: () => void
	onAnonymize?: () => void
}

/** Customer detail header: name, type/status badges, external id, source and the actions (organism). */
export function CustomerHeaderView(props: CustomerHeaderViewProps): ReactElement {
	const { t } = props
	const actions: Array<[string, (() => void) | undefined, 'primary' | 'secondary' | 'danger']> = [
		['actions.edit', props.onEdit, 'secondary'],
		['actions.reassign', props.onReassign, 'secondary'],
		['actions.convert', props.onConvert, 'primary'],
		['actions.anonymize', props.onAnonymize, 'danger'],
	]
	return (
		<header className="flex flex-col gap-2 border-b border-ds-border pb-4">
			<h1 className="text-ds-lg font-semibold text-ds-text">
				{props.anonymized ? t('detail.anonymized') : props.fullName}
			</h1>
			<div className="flex flex-wrap items-center gap-2 text-ds-sm text-ds-text-muted">
				<Badge variant={props.type === 'customer' ? 'primary' : 'muted'}>{t(`type.${props.type}`)}</Badge>
				<Badge variant={props.status === 'active' ? 'success' : 'muted'}>{t(`status.${props.status}`)}</Badge>
				{props.externalId ? <span>{`${t('detail.externalId')}: ${props.externalId}`}</span> : null}
				<span>{`${t('detail.source')}: ${t(`source.${props.source}`)}`}</span>
			</div>
			<div className="flex gap-2">
				{actions.map(([label, onPress, variant]) =>
					onPress ? (
						<Button key={label} size="sm" variant={variant} onPress={onPress}>
							{t(label)}
						</Button>
					) : null,
				)}
			</div>
		</header>
	)
}
